/**
 * Markdown 清洗逻辑(无 JSX,纯字符串处理)
 *
 * 从 formatMarkdown.tsx 拆分出来(P2-M11),负责:
 * - 流式场景下不完整 markdown 结构的检测与截断
 * - 未闭合内联标记的补全(粗体/斜体/代码)
 *
 * 截断策略:不完整的链接/标签/表格行直接截断,暂不显示;
 * 下一帧更多 chunk 到达后结构变完整自然显示,已显示内容永不会被移除或替换,
 * 彻底消除"内容出现→消失→再出现"的闪烁。
 */

/** 不完整表格行判定:与 isPotentialTableRow 判定标准一致 */
const isIncompleteTableRow = (line: string): boolean => {
  const t = line.trim();
  // 标准格式:以 | 开头但不以 | 结尾
  if (t.startsWith('|') && !t.endsWith('|')) return true;
  // 宽松格式:包含至少 2 个 | 但不以 | 结尾(可能是被截断的无前导 | 表格行)
  const pipeCount = (t.match(/\|/g) || []).length;
  if (pipeCount >= 2 && !t.endsWith('|')) {
    const segments = t.split('|').filter(s => s.trim().length > 0);
    if (segments.length >= 2) return true;
  }
  return false;
};

/**
 * 流式场景下处理不完整 Markdown 结构。
 *
 * 补全类处理(不会导致闪烁):
 *   - 未闭合的加粗 ** → 补 **
 *   - 未闭合的斜体 * → 补 *
 *   - 未闭合的行内代码 ` → 补 `
 *   - 未闭合代码块/表格 → 由 _renderFormattedMessageInnerCore 末尾统一刷新
 *
 * 截断类处理(消除闪烁的关键):
 *   - 不完整的链接/标签/表格行 → 截断到不完整结构之前,下一帧自然补全
 */
export const stripIncompleteMarkdown = (text: string): string => {
  let processed = text;

  // 追踪代码块状态,避免对 ``` 内的内容做内联标记计数
  const countOutsideCodeBlocks = (text2: string, marker: string | RegExp): number => {
    let inCb = false;
    let count = 0;
    for (const line of text2.split('\n')) {
      if (line.trimStart().startsWith('```')) { inCb = !inCb; continue; }
      if (inCb) continue;
      if (typeof marker === 'string') {
        count += (line.match(new RegExp(marker.replace(/\*/g, '\\*'), 'g')) || []).length;
      } else {
        count += (line.match(marker) || []).length;
      }
    }
    return count;
  };

  // 未闭合粗体 **
  if (countOutsideCodeBlocks(processed, '**') % 2 === 1) {
    processed = processed.replace(/\s+$/, '') + '**';
  }

  // 未闭合斜体 *(先排除 ** 后计数)
  if (countOutsideCodeBlocks(processed.replace(/\*\*/g, ''), /\*/) % 2 === 1) {
    processed = processed.replace(/\s+$/, '') + '*';
  }

  // 未闭合内联代码 `
  if (countOutsideCodeBlocks(processed, /`/) % 2 === 1) {
    processed = processed.replace(/\s+$/, '') + '`';
  }

  // 不完整链接 [text](url → 截断到 [text](url 之前
  const twoLevelParenPartial = '(?:[^()]|\\((?:[^()]|\\([^()]*\\))*\\))*';
  const incompleteLinkRegex = new RegExp(`\\[([^\\]]*)\\]\\(${twoLevelParenPartial}$`);
  const incompleteLinkMatch = processed.match(incompleteLinkRegex);
  if (incompleteLinkMatch) {
    processed = processed.replace(new RegExp(`\\[([^\\]]*)\\]\\(${twoLevelParenPartial}$`), '');
  }

  // 不完整标签 [text → 截断到 [text 之前
  const incompleteLabelRegex = /\[([^[\]]*)$/;
  const incompleteLabelMatch = processed.match(incompleteLabelRegex);
  if (incompleteLabelMatch) {
    processed = processed.replace(/\[([^[\]]*)$/, '');
  }

  // 不完整表格行 → 截断到不完整行之前(已完成的行保持不变,不完整行下一帧补全后自然显示)
  const lines = processed.split('\n');
  const result: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isIncompleteTableRow(line)) {
      break;
    }
    result.push(line);
    i++;
  }

  return result.join('\n');
};
